/**
 * Card boundary detection + perspective rectification.
 *
 * Runs entirely in the browser on a downscaled working buffer:
 *   1. Estimate the backdrop colour from a border ring.
 *   2. Build a foreground mask (luminance OR chroma distance from backdrop).
 *   3. Keep the largest connected component (drops hands, stray paper).
 *   4. Take the four rotated-extreme corners of that component — this
 *      recovers a rotated / tilted rectangle without needing Hough lines.
 *   5. Solve the 8-DOF homography and inverse-map with bilinear sampling.
 */

import { ctx2d, makeCanvas, type AnyCanvas } from "./canvas";
import { RECTIFY_MAX_EDGE } from "./constants";

export type Point = { x: number; y: number };
export type Quad = [Point, Point, Point, Point]; // TL, TR, BR, BL

export type DetectResult = {
  quad: Quad;
  confident: boolean;
  coverage: number;
  /** 0–1 detection confidence. Auto-crop only runs above CONFIDENCE_GATE. */
  score: number;
};

/**
 * Auto-crop gate. Below this the pipeline keeps the seller's framing and the
 * uploader offers "Adjust crop" — we never guess at the cost of a lost corner.
 */
export const CONFIDENCE_GATE = 0.95;

/** Outward padding kept around the detected card, as a fraction of its size. */
export const QUAD_PADDING = 0.04;

/**
 * Expand a quad outward from its centroid. Detection lands on the card's
 * outer border; padding guarantees the full border and all four corners
 * survive rectification instead of being shaved off.
 */
export function padQuad(quad: Quad, pad = QUAD_PADDING, bounds?: { w: number; h: number }): Quad {
  const cx = (quad[0].x + quad[1].x + quad[2].x + quad[3].x) / 4;
  const cy = (quad[0].y + quad[1].y + quad[2].y + quad[3].y) / 4;
  const k = 1 + pad;
  return quad.map((p) => {
    const x = cx + (p.x - cx) * k;
    const y = cy + (p.y - cy) * k;
    if (!bounds) return { x, y };
    return {
      x: Math.min(bounds.w, Math.max(0, x)),
      y: Math.min(bounds.h, Math.max(0, y)),
    };
  }) as Quad;
}

const WORK_EDGE = 480;

function lum(d: Uint8ClampedArray, i: number): number {
  return 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
}

/** Detect the card quad in a full-size canvas. Coordinates are in source px. */
export function detectCardQuad(source: AnyCanvas): DetectResult {
  const SW = (source as HTMLCanvasElement).width;
  const SH = (source as HTMLCanvasElement).height;
  const scale = Math.min(1, WORK_EDGE / Math.max(SW, SH));
  const w = Math.max(8, Math.round(SW * scale));
  const h = Math.max(8, Math.round(SH * scale));

  const work = makeCanvas(w, h);
  const wctx = ctx2d(work);
  wctx.imageSmoothingEnabled = true;
  wctx.imageSmoothingQuality = "high";
  wctx.drawImage(source as CanvasImageSource, 0, 0, w, h);
  const { data } = wctx.getImageData(0, 0, w, h);

  // --- backdrop estimate from a 4% border ring -------------------------
  const ring = Math.max(2, Math.round(Math.min(w, h) * 0.04));
  let br = 0, bg = 0, bb = 0, n = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const onRing = x < ring || y < ring || x >= w - ring || y >= h - ring;
      if (!onRing) continue;
      const i = (y * w + x) * 4;
      br += data[i]; bg += data[i + 1]; bb += data[i + 2]; n++;
    }
  }
  br /= n; bg /= n; bb /= n;
  const bl = 0.299 * br + 0.587 * bg + 0.114 * bb;

  // --- foreground mask --------------------------------------------------
  const mask = new Uint8Array(w * h);
  const LUM_T = 26;
  const RGB_T = 34;
  for (let p = 0, i = 0; p < w * h; p++, i += 4) {
    const dl = Math.abs(lum(data, i) - bl);
    const dr = Math.abs(data[i] - br);
    const dg = Math.abs(data[i + 1] - bg);
    const db = Math.abs(data[i + 2] - bb);
    const chroma = Math.max(dr, dg, db);
    mask[p] = dl > LUM_T || chroma > RGB_T ? 1 : 0;
  }

  // --- largest connected component (4-way flood, iterative) -------------
  const labels = new Int32Array(w * h).fill(-1);
  const stack = new Int32Array(w * h);
  let best = -1;
  let bestSize = 0;
  let label = 0;
  for (let s = 0; s < w * h; s++) {
    if (mask[s] === 0 || labels[s] !== -1) continue;
    let sp = 0;
    stack[sp++] = s;
    labels[s] = label;
    let size = 0;
    while (sp > 0) {
      const p = stack[--sp];
      size++;
      const px = p % w;
      const py = (p / w) | 0;
      if (px > 0 && mask[p - 1] && labels[p - 1] === -1) { labels[p - 1] = label; stack[sp++] = p - 1; }
      if (px < w - 1 && mask[p + 1] && labels[p + 1] === -1) { labels[p + 1] = label; stack[sp++] = p + 1; }
      if (py > 0 && mask[p - w] && labels[p - w] === -1) { labels[p - w] = label; stack[sp++] = p - w; }
      if (py < h - 1 && mask[p + w] && labels[p + w] === -1) { labels[p + w] = label; stack[sp++] = p + w; }
    }
    if (size > bestSize) { bestSize = size; best = label; }
    label++;
  }

  const coverage = bestSize / (w * h);
  const fallback = (): DetectResult => ({
    score: 0,
    quad: [
      { x: 0, y: 0 },
      { x: SW, y: 0 },
      { x: SW, y: SH },
      { x: 0, y: SH },
    ],
    confident: false,
    coverage,
  });

  if (best < 0 || coverage < 0.06) return fallback();

  // --- rotated extremes give the four corners of a tilted rectangle -----
  let tl = -1, tr = -1, brI = -1, blI = -1;
  let tlV = Infinity, trV = -Infinity, brV = -Infinity, blV = Infinity;
  for (let p = 0; p < w * h; p++) {
    if (labels[p] !== best) continue;
    const x = p % w;
    const y = (p / w) | 0;
    const sum = x + y;
    const diff = x - y;
    if (sum < tlV) { tlV = sum; tl = p; }
    if (sum > brV) { brV = sum; brI = p; }
    if (diff > trV) { trV = diff; tr = p; }
    if (diff < blV) { blV = diff; blI = p; }
  }

  const toPoint = (p: number): Point => ({
    x: ((p % w) / scale),
    y: (((p / w) | 0) / scale),
  });
  const quad: Quad = [toPoint(tl), toPoint(tr), toPoint(brI), toPoint(blI)];

  // Sanity: the quad must be convex-ish, big enough and roughly card-shaped.
  const area = polygonArea(quad);
  const frameArea = SW * SH;
  const top = dist(quad[0], quad[1]);
  const right = dist(quad[1], quad[2]);
  const bottom = dist(quad[2], quad[3]);
  const left = dist(quad[3], quad[0]);
  const parallelOk =
    Math.abs(top - bottom) / Math.max(top, bottom, 1) < 0.35 &&
    Math.abs(left - right) / Math.max(left, right, 1) < 0.35;
  const fill = area / frameArea;

  // --- confidence -------------------------------------------------------
  // Four independent signals, each 0–1. The product is deliberately harsh:
  // one bad signal is enough to fall back to the seller's own framing.
  const parallelScore = clamp01(
    1 -
      Math.max(
        Math.abs(top - bottom) / Math.max(top, bottom, 1),
        Math.abs(left - right) / Math.max(left, right, 1),
      ) / 0.2,
  );
  // Trading cards, slabs, sleeves and top loaders all sit between 0.55 and
  // 0.78 short/long. Portrait and landscape both qualify — orientation is
  // handled downstream, not here.
  const shortSide = Math.min((top + bottom) / 2, (left + right) / 2);
  const longSide = Math.max((top + bottom) / 2, (left + right) / 2);
  const ratio = shortSide / Math.max(1, longSide);
  const aspectScore = clamp01(1 - Math.abs(ratio - 0.7) / 0.22);
  // Corners should be square; skew beyond ~18° means the mask leaked.
  const squareScore = clamp01(1 - maxCornerSkew(quad) / 0.32);
  const fillScore = clamp01((fill - 0.1) / 0.2) * clamp01((0.99 - fill) / 0.1);
  const score = parallelScore * aspectScore * squareScore * fillScore;

  const confident = fill > 0.12 && fill < 0.995 && parallelOk && score >= CONFIDENCE_GATE;

  if (!confident) return { ...fallback(), coverage, score };
  // Pad outward so the card's own border is never cropped into.
  return { quad: padQuad(quad, QUAD_PADDING), confident: true, coverage, score };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Largest deviation from a right angle at any corner, in radians. */
function maxCornerSkew(q: Quad): number {
  let worst = 0;
  for (let i = 0; i < 4; i++) {
    const prev = q[(i + 3) % 4];
    const cur = q[i];
    const next = q[(i + 1) % 4];
    const a = Math.atan2(prev.y - cur.y, prev.x - cur.x);
    const b = Math.atan2(next.y - cur.y, next.x - cur.x);
    let d = Math.abs(a - b);
    while (d > Math.PI) d = Math.abs(d - 2 * Math.PI);
    worst = Math.max(worst, Math.abs(d - Math.PI / 2));
  }
  return worst;
}

function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function polygonArea(q: Quad): number {
  let a = 0;
  for (let i = 0; i < 4; i++) {
    const p = q[i];
    const n = q[(i + 1) % 4];
    a += p.x * n.y - n.x * p.y;
  }
  return Math.abs(a) / 2;
}

/**
 * Solve the homography mapping destination rect (0..dw, 0..dh) to the source
 * quad, so we can inverse-sample. Returns the 8 coefficients.
 */
function solveHomography(quad: Quad, dw: number, dh: number): number[] {
  const dst: Point[] = [
    { x: 0, y: 0 },
    { x: dw, y: 0 },
    { x: dw, y: dh },
    { x: 0, y: dh },
  ];
  const A: number[][] = [];
  const B: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x: u, y: v } = dst[i];
    const { x, y } = quad[i];
    A.push([u, v, 1, 0, 0, 0, -u * x, -v * x]);
    B.push(x);
    A.push([0, 0, 0, u, v, 1, -u * y, -v * y]);
    B.push(y);
  }
  return gaussianSolve(A, B);
}

function gaussianSolve(A: number[][], B: number[]): number[] {
  const n = B.length;
  const M = A.map((row, i) => [...row, B[i]]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (Math.abs(M[pivot][col]) < 1e-12) continue;
    [M[col], M[pivot]] = [M[pivot], M[col]];
    const pv = M[col][col];
    for (let c = col; c <= n; c++) M[col][c] /= pv;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col];
      if (f === 0) continue;
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
    }
  }
  return M.map((row) => row[n]);
}

/**
 * Perspective-rectify the quad out of `source` into an upright rectangle.
 * Landscape results are rotated to portrait — trading cards are portrait.
 */
export function rectifyCard(
  source: AnyCanvas,
  quad: Quad,
  opts: { preserveOrientation?: boolean } = {},
): {
  canvas: AnyCanvas;
  rotated: boolean;
} {
  const SW = (source as HTMLCanvasElement).width;
  const SH = (source as HTMLCanvasElement).height;

  const widthEst = (dist(quad[0], quad[1]) + dist(quad[3], quad[2])) / 2;
  const heightEst = (dist(quad[0], quad[3]) + dist(quad[1], quad[2])) / 2;
  const longEst = Math.max(widthEst, heightEst);
  const scale = Math.min(1, RECTIFY_MAX_EDGE / Math.max(1, longEst));
  const dw = Math.max(16, Math.round(widthEst * scale));
  const dh = Math.max(16, Math.round(heightEst * scale));

  const h = solveHomography(quad, dw, dh);
  const [h0, h1, h2, h3, h4, h5, h6, h7] = h;

  const sctx = ctx2d(source);
  const src = sctx.getImageData(0, 0, SW, SH);
  const sd = src.data;
  const out = new ImageData(dw, dh);
  const od = out.data;

  for (let v = 0; v < dh; v++) {
    for (let u = 0; u < dw; u++) {
      const denom = h6 * u + h7 * v + 1;
      const sx = (h0 * u + h1 * v + h2) / denom;
      const sy = (h3 * u + h4 * v + h5) / denom;
      const o = (v * dw + u) * 4;
      if (sx < 0 || sy < 0 || sx > SW - 1 || sy > SH - 1) {
        od[o + 3] = 0;
        continue;
      }
      // bilinear sample
      const x0 = Math.floor(sx);
      const y0 = Math.floor(sy);
      const x1 = Math.min(SW - 1, x0 + 1);
      const y1 = Math.min(SH - 1, y0 + 1);
      const fx = sx - x0;
      const fy = sy - y0;
      const i00 = (y0 * SW + x0) * 4;
      const i10 = (y0 * SW + x1) * 4;
      const i01 = (y1 * SW + x0) * 4;
      const i11 = (y1 * SW + x1) * 4;
      for (let c = 0; c < 4; c++) {
        const top = sd[i00 + c] * (1 - fx) + sd[i10 + c] * fx;
        const bot = sd[i01 + c] * (1 - fx) + sd[i11 + c] * fx;
        od[o + c] = top * (1 - fy) + bot * fy;
      }
      od[o + 3] = 255;
    }
  }

  const flat = makeCanvas(dw, dh);
  ctx2d(flat).putImageData(out, 0, 0);

  // A genuinely landscape card (some sports, MTG lands, full-art promos) must
  // stay landscape. Only rotate when the caller asks for an upright result.
  if (dw <= dh || opts.preserveOrientation) return { canvas: flat, rotated: false };

  // Rotate landscape → portrait.
  const rot = makeCanvas(dh, dw);
  const rctx = ctx2d(rot);
  rctx.translate(dh / 2, dw / 2);
  rctx.rotate(-Math.PI / 2);
  rctx.drawImage(flat as CanvasImageSource, -dw / 2, -dh / 2);
  return { canvas: rot, rotated: true };
}
